// Quote Builder — per-supplier drilling-charge cache + resolver.
//
// Suppliers set a "drilling charge per hole" in their catalogue config. When
// the retailer configures a door with hinge drilling, the QB appends a
// separate "Drilling charge (N holes)" line item priced at
// (drillingChargePerHole × holeQty). NULL / 0 → no line is added.
//
// Mirrors the cache shape used by `discounts.ts` so callers can warm one
// supplier per mount and read synchronously when adding items.

import { api } from "@tkos/api-client";

const CACHE = new Map<string, number | null>();       // key = supplier slug (lowercased)
const LOADING = new Set<string>();                     // de-dupe in-flight loads

export async function loadDrillingChargeFor(supplierSlug: string): Promise<void> {
  const key = supplierSlug.toLowerCase();
  if (CACHE.has(key) || LOADING.has(key)) return;
  LOADING.add(key);
  try {
    const data = await api<{ ok: boolean; drillingChargePerHole?: number | null }>(
      `/catalogue/drilling-charge?supplier=${encodeURIComponent(key)}`,
    );
    const v = data.drillingChargePerHole;
    CACHE.set(key, typeof v === "number" && Number.isFinite(v) && v > 0 ? v : null);
  } catch {
    CACHE.set(key, null);
  } finally {
    LOADING.delete(key);
  }
}

/** Returns the cached per-hole charge or null when the supplier hasn't set one. */
export function resolveDrillingCharge(supplierSlug: string): number | null {
  return CACHE.get(supplierSlug.toLowerCase()) ?? null;
}

/** For tests / debugging — clears the in-memory cache. */
export function _resetDrillingChargeCacheForTests(): void {
  CACHE.clear();
  LOADING.clear();
}
